import { BreakpointTrackerContext } from '@jutro/layout';
import { useModal } from '@jutro/components';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { LoadSaveService } from "twia-capability-quoteandbind-residential/index.js";
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { ENTQuestionSetComponent as QuestionSetComponent_Ext } from 'ent-capability-quoteandbind-questionsets-ui/index.js';
import { ENTValidationUtil } from 'ent-capability-quoteandbind-questionsets-js/index.js';
import metadata from './QualificationPage.metadata.json5';
import messages from './QualificationPage.messages.js';
import tfpaWizardStyles from 'tfpa-portals-wizard-components-ui/common/TFPAWizard.module.scss'
import twiaWizardStyles from 'twia-portals-wizard-components-ui/common/TWIAWizard.module.scss'
import TWIACleanseObj from '../../../twia-capability-quoteandbind-TWIAResidential-react/util/TWIACleanseObjUtil.js';
import { getHOLOBPath, getHOLOB } from '../../util/EntHomeownersUtil';
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil.js';
import { wizardMessages } from 'tfpa-portals-wizard-components-ui';

const LOB_PATH = {
    Homeowners: 'preQualQuestionSets.children[0]',
    PolicyType: 'preQualQuestionSets.children[1]',
};

function ENTQualificationPage(props) {
    const { id, wizardData: submissionVM, updateWizardData, steps } = props;
    const [showErrors, setShowErrors] = useState(false);
    const [warningMessages, setWarningMessages] = useState([]);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const {
        isComponentValid,
        initialValidation,
        onValidate,
    } = useValidation(id);
    const lobCode = _.get(submissionVM, 'baseData.productCode.value');
    const [isLoadingData, setIsLoadingData] = useState(false);
    const { showAlert, showConfirm } = useModal();
    //DE15433
    const lobData = getHOLOB(submissionVM);
    const lobPath = getHOLOBPath(submissionVM);
    const [selectedPolicyType, setSelectedPolicyType] = useState(_.get(lobData, 'policyType_TWIA.value.code'));
    const selectedUWCompany = _.get(submissionVM, 'baseData.associationName_TWIA.value.code');
    const isTFPA = selectedUWCompany === 'tfpa';
    const propertyInfoStep = steps.find((item) => item.path === "/propertydetails");
    const [propertyDetailsStep, setPropertyDetailsStep] = useState(propertyInfoStep);
    const [initialValidationResult, setInitialValidationResult] = useState(null);


    const resetLOBData = () => {
        // US26300
        _.set(lobData, 'coverables.yourHome.value', undefined);
        _.set(lobData, 'coverables.construction.value', undefined);
        _.set(lobData, 'offerings.value', undefined);
        _.set(lobData, 'coverables.rating.value', undefined);
        // DE19867 - TFPA - PE - user getting DTO error message when continuing submission
        if (isTFPA && moment(_.get(submissionVM, 'baseData.periodStartDate.value')).isBefore(moment(), 'day')) {
            _.set(submissionVM, 'baseData.periodStartDate.value', LocalDateUtil.today());
        }
        _.unset(submissionVM.value, 'bindData');
        TWIACleanseObj.resetAspectsNotVisibleValues(submissionVM);
        updateWizardData(submissionVM);
    };

    const onNext = useCallback(async () => {

        if (!isComponentValid) {
            setShowErrors(true);

            return false
        }

        resetLOBData();

        try {
            const updatedSubmission = await LoadSaveService.validatePreQualAndUpdateDraftSubmission_TWIA(
                submissionVM.value,
                authHeader
            );

            _.extend(submissionVM.value, updatedSubmission);

            updateWizardData(submissionVM);
    
            return submissionVM;
            
        } catch (err) {
            // US26877 handle prequalification warnings
            const {errorMessage, isWarning, messageType} = ENTValidationUtil.handleQualificationError(err);
            const title = isWarning ? messages.ENTResidentialQualificationElegibilityWarningTitle:
                                      messages.ENTResidentialQualificationElegibilityErrorTitle;
            const existingWarning = isWarning && warningMessages.includes(errorMessage);
            isWarning && warningMessages.push(errorMessage);
            setWarningMessages(warningMessages);

            if (!isWarning || !existingWarning) {
                if(errorMessage.includes("DTO")){
                    ErrorPopupUtil.showSaveErrorPopup(showAlert, !isTFPA);
                }else{
                    showAlert({
                        title: title,
                        message: errorMessage,
                        status: messageType,
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.close,
                    }).catch(_.noop);
                }
            }
  
            return existingWarning ? submissionVM : false; 
        }
        
    }, [isComponentValid, resetLOBData, submissionVM, authHeader, updateWizardData,
         warningMessages, showAlert, isTFPA]);



    const writeValue = useCallback(
        (value, path) => {
            const newSubmissionVM = _.clone(submissionVM);

            _.set(newSubmissionVM, path, value);
            updateWizardData(newSubmissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const saveAndRender = useCallback(async(value, path, line) => {
        const oldValue = _.get(lobData, `${LOB_PATH[line]}.answers['${path}']`);
        if(value === oldValue){
            return;
        }
        setIsLoadingData(true);
        writeValue(value, `${lobPath}.${LOB_PATH[line]}.value.answers['${path}']`);
        resetLOBData();
        // US26345 - changed below service call from saveSubmission to updateDraftSubmission 
        // as saveSubmission was giving error after quote
        const updatedSubmission = await LoadSaveService.updateDraftSubmission(
            submissionVM.value,
            authHeader);
            TWIACleanseObj.resetAspectsNotVisibleValues(updatedSubmission)
        _.extend(submissionVM.value, updatedSubmission);
        updateWizardData(submissionVM);
        setIsLoadingData(false);
    }, [submissionVM, writeValue, updateWizardData, authHeader])


    const handleOnPolicyTypeChange = useCallback(async (value, path)=>{
        const oldValue = _.get(submissionVM, `${path}.value.code`);
        if(value === oldValue){
            return;
        }

        setIsLoadingData(true);
        writeValue(value, path);
        resetLOBData();
        const updatedSubmission = await LoadSaveService.changePolicyTypeAndUpdateDraftSubmission_TWIA(
            submissionVM.value,
            authHeader
        );
            
     
       _.extend(submissionVM.value, updatedSubmission);

       updateWizardData(submissionVM);
       setSelectedPolicyType(_.get(lobData, 'policyType_TWIA.value'))
       setIsLoadingData(false);

    },[authHeader, submissionVM, updateWizardData, writeValue]);
 
    const onPrevious = useCallback(() => {
        if (!isComponentValid) {
           return showConfirm({
                title: wizardMessages.TWIAWizardModalTitle,
                message: wizardMessages.TWIAWizardModalMessage,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: wizardMessages.TWIAWizardModalConfirm,
                cancelButtonText: wizardMessages.TWIAWizardModalCancel,
            }).then((results) => {
                if (results === 'cancel' || results === 'close') {
                    setShowErrors(true);
                    return false;
                }                
                return true;
            }, _.noop)
        }        
    }, [isComponentValid]);
 
    const formatTitleTypekey = (msg) => {
        return translator({id: `questionkey.QuestionSet_${msg}.Name`})
    }

    useEffect(() => {
        const initialValidationResponse = async () => {
            setInitialValidationResult(await initialValidation());
        }
        initialValidationResponse();
    }, []);

    const isFieldDisabled = useMemo(() => {
        setPropertyDetailsStep(propertyInfoStep);
        if (propertyDetailsStep?.visited && !propertyInfoStep?.visited) {
            return !isTFPA && propertyDetailsStep?.visited;
        }
        return !isTFPA && (propertyInfoStep?.visited || !_.isEmpty(_.get(lobData, 'coverables.yourHome.value')) || (initialValidationResult && _.get(lobData, 'preQualAnswersValid_TWIA.value')));
    }, [propertyInfoStep, initialValidationResult]);

    const overrideProps = {
        '@field': {
            // apply to all fields
            alwaysShowPlaceholder: false,
     
        },
        generalQuestionSet: {
            path: `${lobPath}.${LOB_PATH[lobCode]}`,
            lobCode,
            title: formatTitleTypekey(_.get(lobData, `${LOB_PATH[lobCode]}.value.code`)),
            onValidate,
            showErrors,
            visible: !isLoadingData,
            saveAndRender: (value, path) => saveAndRender(value, path, lobCode),
            //DE15433
            selectedPolicyType,
            selectedUWCompany
        },
        propertyQuestionSet: {
            path: `${lobPath}.${LOB_PATH['PolicyType']}`,
            lobCode,
            // US26878 show the correct property eligiblity message
            title: translator(isTFPA ? messages.TFPAResidentialQualificationPropertyTitle : messages.TWIAResidentialQualificationPropertyTitle),
            onValidate,
            showErrors,
            visible: !isLoadingData,
            saveAndRender: (value, path) => saveAndRender(value, path, 'PolicyType'),
            isStructureTypeDisabled: ['TWRD', 'TWRP'].includes(selectedPolicyType) && isFieldDisabled
        },
        generalQuestionsContainer:{
            visible: _.get(lobData, 'policyType_TWIA.value') ? true : false,
        },
        propertyQuestionsContainer: {
            visible:  _.get(lobData, 'policyType_TWIA.value') ? true : false,
        },
        policyType: {
            onValueChange: handleOnPolicyTypeChange,
            path: `${lobPath}.policyType_TWIA`,
            validationMessages: !_.isUndefined(_.get(lobData, 'policyType_TWIA.value')) ? [] : [translator(messages.required)],  // DE15920
            disabled: isFieldDisabled
        },
        questionSetLoader: {
            visible: isLoadingData
        }

    };

    const resolvers = {
        resolveComponentMap: { questionset: QuestionSetComponent_Ext },
        resolveClassNameMap: isTFPA ? tfpaWizardStyles : twiaWizardStyles,
        resolveCallbackMap: {
        onValidate,
        },
    };
    
    // DE16367
    const policyList = ['TWRC', 'TWRM']

    const skip = useCallback(async vm => {
        const validAnswers =  _.get(lobData, 'preQualAnswersValid_TWIA.value');

        // DE16309
        if (!isTFPA && _.includes(policyList, vm.lobData.homeowners.policyType_TWIA?.value?.code) && (await initialValidation()) && validAnswers) {
            return true;
        }

        return (await initialValidation()) && validAnswers;
    }, []);

    return (
      
        <WizardPage
            id={id}
            showPrevious={isTFPA ? false : true}
            onNext={onNext}
            skipWhen={skip}
            onPrevious={onPrevious}
        >
            
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
            />
        </WizardPage>
    );
}

ENTQualificationPage.propTypes = wizardProps;
export default ENTQualificationPage;
